import React, { useState } from "react";
import type { Violation } from "../types";

export interface ViolationsPanelProps {
  violations: Violation[];
  onFix: (violation: Violation) => void;
  onIgnore: (violationId: string) => void;
  fixingIds: Set<string>;
  fixedIds: Set<string>;
}

const ViolationsPanel: React.FC<ViolationsPanelProps> = ({
  violations,
  onFix,
  onIgnore,
  fixingIds,
  fixedIds,
}) => {
  const [ignoredIds, setIgnoredIds] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState<"all" | "ERC" | "DRC">("all");

  if (violations.length === 0) return null;

  const visible = violations.filter((v) => {
    if (ignoredIds.has(v.id)) return false;
    if (filter !== "all" && v.source !== filter) return false;
    return true;
  });

  const ercCount = violations.filter((v) => v.source === "ERC" && !ignoredIds.has(v.id)).length;
  const drcCount = violations.filter((v) => v.source === "DRC" && !ignoredIds.has(v.id)).length;
  const errorCount = visible.filter((v) => v.severity === "error").length;
  const warnCount = visible.filter((v) => v.severity === "warning").length;

  const handleIgnore = (id: string) => {
    setIgnoredIds((prev) => new Set(prev).add(id));
    onIgnore(id);
  };

  return (
    <div className="violations-panel" role="region" aria-label="Design Violations">
      <div className="violations-header">
        <h2>Design Violations</h2>
        <div className="violations-summary">
          <span className="v-badge v-error">{errorCount} errors</span>
          <span className="v-badge v-warn">{warnCount} warnings</span>
        </div>
      </div>

      <div className="violations-filters">
        <button
          className={`v-filter ${filter === "all" ? "v-filter-active" : ""}`}
          onClick={() => setFilter("all")}
        >
          All ({ercCount + drcCount})
        </button>
        <button
          className={`v-filter ${filter === "ERC" ? "v-filter-active" : ""}`}
          onClick={() => setFilter("ERC")}
        >
          ERC ({ercCount})
        </button>
        <button
          className={`v-filter ${filter === "DRC" ? "v-filter-active" : ""}`}
          onClick={() => setFilter("DRC")}
        >
          DRC ({drcCount})
        </button>
      </div>

      <ul className="violations-list">
        {visible.map((v) => {
          const isFixing = fixingIds.has(v.id);
          const isFixed = fixedIds.has(v.id);
          return (
            <li key={v.id} className={`violation-item v-${v.severity} ${isFixed ? "v-fixed" : ""}`}>
              <div className="violation-info">
                <span className={`v-severity-dot v-dot-${v.severity}`} />
                <span className="v-source">{v.source}</span>
                <span className="v-message">{v.message}</span>
                {v.location && <span className="v-location">@ {v.location}</span>}
              </div>
              <div className="violation-actions">
                {isFixed ? (
                  <span className="v-fixed-label">✓ Fixed</span>
                ) : isFixing ? (
                  <span className="v-fixing-label">⟳ Fixing…</span>
                ) : (
                  <>
                    <button className="v-btn v-btn-fix" onClick={() => onFix(v)}>
                      Fix
                    </button>
                    <button className="v-btn v-btn-ignore" onClick={() => handleIgnore(v.id)}>
                      Ignore
                    </button>
                  </>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {visible.length === 0 && (
        <p className="violations-empty">
          {ignoredIds.size > 0 ? "All violations handled." : "No violations to show."}
        </p>
      )}
    </div>
  );
};

export default ViolationsPanel;
